import React, { useContext, useState } from 'react';
import { AuthContext } from '../shared/Context/auth-context';
import { useSessionState } from '../shared/hooks/session-state-hook';
import { sessionKey } from '../shared/util/session-storage';
import { useCareMutation } from './care-api';
import { formatCareDate } from './care-utils';

const CarePlan = ({ record, onSaved }) => {
  const auth = useContext(AuthContext);
  const patient = record.viewer.role === 'patient';
  const canCreate = record.viewer.role === 'doctor' && record.primaryDoctorId === auth.doctorId;
  const [draft, setDraft, clearDraft, stored] = useSessionState(
    sessionKey(`doctor:${auth.doctorId}`, `care:${record.patient.id}:task`),
    { title: '', instructions: '', dueAt: '' }
  );
  const { mutate, saving, error } = useCareMutation();
  const [validation, setValidation] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const tasks = [...(record.tasks || [])].sort(
    (a, b) =>
      (a.status === 'completed') - (b.status === 'completed') ||
      new Date(a.dueAt) - new Date(b.dueAt)
  );
  const submit = async (event) => {
    event.preventDefault();
    if (!draft.title.trim() || !draft.instructions.trim() || !draft.dueAt) {
      setValidation('Add a task, instructions, and a due date.');
      return;
    }
    const dueAt = new Date(`${draft.dueAt}T23:59:59`);
    if (!Number.isFinite(dueAt.getTime()) || dueAt < new Date()) {
      setValidation('Choose today or a future date.');
      return;
    }
    setValidation(null);
    try {
      await mutate(`/patients/${record.patient.id}/tasks`, 'POST', {
        title: draft.title.trim(),
        instructions: draft.instructions.trim(),
        dueAt: dueAt.toISOString()
      });
      clearDraft();
      setShowCreate(false);
      onSaved('The task was added to the patient’s care plan.');
    } catch {}
  };
  const complete = async (id) => {
    try {
      await mutate(`/patients/${record.patient.id}/tasks/${id}`, 'PATCH', { status: 'completed' });
      onSaved('Your task was marked completed. Your care team can see your update.');
    } catch {}
  };
  return (
    <section className="care-panel" aria-labelledby="care-plan-title">
      <div className="care-panel-heading">
        <div>
          <p className="eyebrow">BETWEEN VISITS</p>
          <h2 id="care-plan-title">Care plan & follow-ups</h2>
          <p className="care-muted">Agreed next steps, with a clear due date.</p>
        </div>
        {canCreate && (
          <button className="button" onClick={() => setShowCreate(true)}>
            Add a task
          </button>
        )}
      </div>
      {showCreate && canCreate && (
        <form className="care-task-form" onSubmit={submit}>
          <fieldset disabled={saving}>
            <div className="care-form-grid">
              <label>
                Task
                <input
                  required
                  maxLength={160}
                  value={draft.title}
                  onChange={(event) => setDraft({ ...draft, title: event.target.value })}
                  placeholder="For example: submit the requested readings"
                />
              </label>
              <label>
                Due date
                <input
                  type="date"
                  required
                  value={draft.dueAt}
                  onChange={(event) => setDraft({ ...draft, dueAt: event.target.value })}
                />
              </label>
              <label className="care-full-width">
                Instructions
                <textarea
                  required
                  maxLength={4000}
                  rows={3}
                  value={draft.instructions}
                  onChange={(event) => setDraft({ ...draft, instructions: event.target.value })}
                />
              </label>
            </div>
            {!stored && (
              <p className="care-error">This task draft could not be kept in tab storage.</p>
            )}
            <div className="care-actions">
              <button className="button" type="submit" disabled={saving}>
                {saving ? 'Saving…' : 'Save task'}
              </button>
              <button
                className="button button--inverse"
                type="button"
                onClick={() => setShowCreate(false)}
              >
                Back to care plan
              </button>
            </div>
          </fieldset>
        </form>
      )}
      {(validation || error) && (
        <p className="care-error" role="alert">
          {validation || error}
        </p>
      )}
      {!tasks.length && (
        <div className="care-empty">
          <h3>No follow-up tasks yet</h3>
          <p>
            {patient
              ? 'Your primary doctor can add agreed next steps here.'
              : 'Tasks from the primary doctor will appear here.'}
          </p>
        </div>
      )}
      <ul className="care-task-list">
        {tasks.map((task) => {
          const overdue =
            task.status !== 'completed' && new Date(task.dueAt).getTime() < Date.now();
          return (
            <li key={task.id}>
              <div>
                <span className={`care-pill ${overdue ? 'care-pill--attention' : ''}`}>
                  {task.status === 'completed'
                    ? 'Completed by patient'
                    : overdue
                      ? 'Overdue'
                      : 'Upcoming'}
                </span>
                <h3>{task.title}</h3>
                <p className="care-prewrap">{task.instructions}</p>
                <p className="care-muted">
                  Due {formatCareDate(task.dueAt, false)} · Added by{' '}
                  {task.author?.name || 'Primary doctor'}
                </p>
                {task.completedAt && (
                  <p className="care-muted">
                    Patient reported completion on {formatCareDate(task.completedAt)}.
                  </p>
                )}
              </div>
              {patient && task.status !== 'completed' && (
                <button
                  className="button button--inverse"
                  disabled={saving}
                  onClick={() => complete(task.id)}
                >
                  Mark completed
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
};
export default CarePlan;
